/**
 * PATH: packages/email-hub/src/feature/components/AttachmentContextMenu.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENT-ATTACHMENTCONTEXTMENU-001
 * PURPOSE: Canonical body of this email component. Lifted from
 *          apps/<tenant>/src/features/email/components/AttachmentContextMenu.tsx
 *          on K3a (see docs/architecture/EMAIL_KANBAN_CUTOVER_ARC.md).
 *          Tenants import this via thin re-export shims; edit
 *          THIS file, not the tenant copies.
 * WHY:     Removes operator-visible drift between tenants —
 *          previously every email feature change had to be
 *          mirrored manually in two tenant trees, and concurrent
 *          agents kept letting one side go stale (BUG-0032 was
 *          the most recent example). One canonical body, both
 *          tenants consume from this package.
 * RELATED:
 *   - apps/<tenant>/src/features/email/components/AttachmentContextMenu.tsx:
 *     thin re-export shim that points back here.
 */

import { useEffect, useRef } from 'react'
import { useCloseOnOutsidePointer } from '@quotech/ui'
import {
  Copy,
  Download,
  Eye,
  ExternalLink,
  FolderOpen,
  Hash,
  Link2,
  Loader2,
  Receipt,
} from 'lucide-react'
import { cn } from '../../_internal/lib/utils'
import type { EmailAttachment } from '../hooks/useEmails'

/**
 * AttachmentContextMenu
 * ---------------------
 *
 * The right-click menu that pops out of an attachment chip. Mirrors
 * the macOS / Outlook attachment context menu — the user expects
 * to right-click a PDF and see "Open / Download / Allocate to Bills"
 * without having to dig through a kebab.
 *
 * Positioning
 *   We render fixed at ``(x, y)`` from the contextmenu event.
 *   AttachmentList owns the (x, y) state so only one menu is open
 *   at a time. We clamp the position so the menu doesn't fall off
 *   the viewport on narrow windows.
 *
 * Dismissal
 *   - click outside (mousedown listener on document)
 *   - Escape key
 *   - clicking any item (parent decides via onClose)
 *   - scrolling the underlying page
 *
 * The "Allocate to Bills" item is always the visual anchor of the
 * menu — it's the one feature that doesn't exist anywhere else in
 * the UI right now, so we lead with it. The rest (Preview, Download,
 * Copy filename, Open in new tab) are quality-of-life duplicates of
 * the chip's primary controls so right-click is a complete substitute
 * for hovering over a chip.
 *
 * The component is intentionally dumb — no fetching, no toasts. The
 * parent owns the side-effects so the menu can be reused for other
 * chips in the future (Bills page, Job page, etc).
 */

export interface AttachmentContextMenuProps {
  attachment: EmailAttachment
  /** Viewport coordinates of the contextmenu event. */
  x: number
  y: number
  /** Disabled while the parent's mark-as-bill request is in flight. */
  allocatingToBill?: boolean
  onClose: () => void
  onPreview: () => void
  onDownload: () => void
  onAllocateToBill: () => void
  onCopyFilename: () => void
  onOpenInNewTab: () => void
  /** Email→Quote ARC § 3.4 (P4 / Scope 1C) — QDrive surface
   *  affordances. Only invoked when the attachment carries the
   *  matching field (``qdrive_file_id`` / ``qdrive_ref_code``).
   *  The whole QDrive section of the menu is hidden when neither
   *  field is populated, so an un-ingested attachment looks
   *  identical to the pre-P4 menu. */
  onOpenInQDrive?: () => void
  onCopyQDriveLink?: () => void
  onCopyQDriveRef?: () => void
}

const MENU_WIDTH = 240
// Approx. number of items × 36px + padding. Used for viewport
// clamping so the menu never gets hidden under the bottom edge.
const MENU_HEIGHT = 240

export default function AttachmentContextMenu({
  attachment,
  x,
  y,
  allocatingToBill = false,
  onClose,
  onPreview,
  onDownload,
  onAllocateToBill,
  onCopyFilename,
  onOpenInNewTab,
  onOpenInQDrive,
  onCopyQDriveLink,
  onCopyQDriveRef,
}: AttachmentContextMenuProps) {
  const ref = useRef<HTMLDivElement>(null)

  // Reused from: packages/ui/src/hooks/useCloseOnOutsidePointer.ts (useCloseOnOutsidePointer)
  useCloseOnOutsidePointer(true, onClose, [ref])
  useEffect(() => {
    const onScroll = () => onClose()
    window.addEventListener('scroll', onScroll, true)
    return () => window.removeEventListener('scroll', onScroll, true)
  }, [onClose])

  // Clamp inside viewport so the menu never overflows. We use the
  // window dimensions at render time — close-enough for one-off
  // menus and avoids ResizeObserver overhead.
  const clampedX = Math.min(x, Math.max(0, window.innerWidth - MENU_WIDTH - 8))
  const clampedY = Math.min(y, Math.max(0, window.innerHeight - MENU_HEIGHT - 8))

  const downloadable = !!attachment.download_url
  // Email→Quote ARC § 3.4 (P4 / Scope 1C) — show the QDrive section
  // only when at least one field is present. Some attachments will
  // have ``qdrive_file_id`` without a ``ref_code`` (the FileRecord
  // exists but the ref hasn't been minted yet — historical rows pre-
  // PR 4Q-5 are the main case). The individual items below gate
  // independently so the user always sees the action that maps to a
  // populated field.
  const hasQDriveFile = !!attachment.qdrive_file_id
  const hasQDriveRef = !!attachment.qdrive_ref_code
  const showQDriveSection = hasQDriveFile || hasQDriveRef

  return (
    <div
      ref={ref}
      role="menu"
      aria-label={`Actions for ${attachment.filename}`}
      className={cn(
        'fixed z-[60] min-w-[240px] rounded-lg border border-border bg-popover',
        'text-popover-foreground shadow-2xl py-1',
        'animate-in fade-in-0 zoom-in-95 duration-100',
      )}
      style={{ top: clampedY, left: clampedX }}
      onContextMenu={(e) => e.preventDefault()}
    >
      <MenuHeader filename={attachment.filename} />

      <MenuDivider />

      <MenuItem
        icon={<Receipt className="h-4 w-4" />}
        label="Allocate to Bills"
        accent
        loading={allocatingToBill}
        onClick={() => {
          onAllocateToBill()
        }}
        title="Mark this as a supplier invoice and open the bill in the Billing Hub"
      />

      <MenuDivider />

      <MenuItem
        icon={<Eye className="h-4 w-4" />}
        label="Preview"
        disabled={!downloadable}
        onClick={() => {
          onClose()
          onPreview()
        }}
      />
      <MenuItem
        icon={<Download className="h-4 w-4" />}
        label="Download"
        disabled={!downloadable}
        onClick={() => {
          onClose()
          onDownload()
        }}
      />
      <MenuItem
        icon={<ExternalLink className="h-4 w-4" />}
        label="Open in new tab"
        disabled={!downloadable}
        onClick={() => {
          onClose()
          onOpenInNewTab()
        }}
      />

      {showQDriveSection && (
        <>
          <MenuDivider />
          {hasQDriveFile && (
            <MenuItem
              icon={<FolderOpen className="h-4 w-4" />}
              label="Open in QDrive"
              onClick={() => {
                onClose()
                onOpenInQDrive?.()
              }}
              title="Open this file in the universal QDrive surface (audit trail, version history, sharing)"
            />
          )}
          {hasQDriveFile && (
            <MenuItem
              icon={<Link2 className="h-4 w-4" />}
              label="Copy QDrive link"
              onClick={() => {
                onClose()
                onCopyQDriveLink?.()
              }}
              title="Copy the canonical /qdrive?file_id=… URL for this attachment"
            />
          )}
          {hasQDriveRef && (
            <MenuItem
              icon={<Hash className="h-4 w-4" />}
              label={`Copy ref ${attachment.qdrive_ref_code}`}
              onClick={() => {
                onClose()
                onCopyQDriveRef?.()
              }}
              title="Copy the short cross-hub reference code (paste into any Quotech surface)"
            />
          )}
        </>
      )}

      <MenuDivider />

      <MenuItem
        icon={<Copy className="h-4 w-4" />}
        label="Copy filename"
        onClick={() => {
          onClose()
          onCopyFilename()
        }}
      />
    </div>
  )
}

function MenuHeader({ filename }: { filename: string }) {
  return (
    <div className="px-3 py-2">
      <p
        className="text-xs font-medium text-foreground truncate"
        title={filename}
      >
        {filename}
      </p>
    </div>
  )
}

function MenuDivider() {
  return <div role="separator" className="my-1 h-px bg-border" />
}

interface MenuItemProps {
  icon: React.ReactNode
  label: string
  onClick: () => void
  disabled?: boolean
  loading?: boolean
  accent?: boolean
  title?: string
}

function MenuItem({
  icon,
  label,
  onClick,
  disabled,
  loading,
  accent,
  title,
}: MenuItemProps) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      disabled={disabled || loading}
      title={title}
      className={cn(
        'w-full flex items-center gap-2.5 px-3 py-1.5 text-sm text-left',
        'transition-colors',
        'focus-visible:outline-none focus-visible:bg-accent',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        !disabled &&
          (accent
            ? 'text-primary hover:bg-primary/10'
            : 'text-foreground hover:bg-accent'),
      )}
    >
      <span
        className={cn(
          'shrink-0',
          accent ? 'text-primary' : 'text-muted-foreground',
        )}
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : icon}
      </span>
      <span className="flex-1 truncate">{label}</span>
    </button>
  )
}
